import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FaCommentDots, FaLock, FaRobot } from 'react-icons/fa'
import { apiClient, buildAuthRequestConfig, getAuthToken } from '../../utils/apiClient'
import { localizedText } from '../../utils/localized'

export const PENDING_ACTIONS_REFRESH_EVENT = 'support:pending-actions-refresh';

// بعد أي تعليق: الواجهة بتبعت الحدث ده عشان القفل يتشال على طول.
export const refreshPendingActions = () => window.dispatchEvent(new Event(PENDING_ACTIONS_REFRESH_EVENT));

const POLL_MS = 60 * 1000;

const getTenantFromPath = (pathname) => pathname.split('/').filter(Boolean)[0] || '';
const getShownTicketId = (pathname) => {
    const match = pathname.match(/\/tickets\/show\/([^/?#]+)/);
    return match ? match[1] : '';
};
const isFreePath = (pathname) => /\/(logout|token)(\/|$)/.test(pathname);

// "إجبار التعليق" من الأعمال المجدولة: لو الأدمن عليه تذكرة لازم يعلّق عليها، كل الصفحات بتتقفل
// (تفاصيل تذاكر تانية، المشاريع، الإنشاء...) لحد ما يعلّق — صفحة التذكرة نفسها بس هي المفتوحة.
const PendingActionGuard = ({ lang = 'ar' }) => {
    const isAr = lang !== 'en';
    const location = useLocation();
    const navigate = useNavigate();
    const [pending, setPending] = useState([]);
    const requestRef = useRef(0);

    const load = useCallback(async () => {
        if (!getAuthToken()) return;
        const requestId = ++requestRef.current;
        try {
            const response = await apiClient.get(
                `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/admin/pending-actions`,
                buildAuthRequestConfig({ validateStatus: () => true })
            );
            if (requestId !== requestRef.current || response?.status !== 200) return;
            setPending(Array.isArray(response?.data?.data) ? response.data.data : []);
        } catch {
            // لو السيرفر مش متاح مانقفلش الشغل.
        }
    }, []);

    useEffect(() => {
        load();
    }, [load, location.pathname]);

    useEffect(() => {
        const timer = window.setInterval(load, POLL_MS);
        const onRefresh = () => load();
        window.addEventListener(PENDING_ACTIONS_REFRESH_EVENT, onRefresh);
        window.addEventListener('focus', onRefresh);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener(PENDING_ACTIONS_REFRESH_EVENT, onRefresh);
            window.removeEventListener('focus', onRefresh);
        };
    }, [load]);

    const tenant = getTenantFromPath(location.pathname);
    const shownTicketId = getShownTicketId(location.pathname);
    const currentPending = useMemo(() => pending.find((item) => String(item._id) === shownTicketId) || null, [pending, shownTicketId]);
    const blocked = pending.length > 0 && !currentPending && !isFreePath(location.pathname);

    if (!pending.length) return null;

    const goTo = (ticket) => navigate(`/${tenant}/tickets/show/${ticket._id}`);

    // على صفحة التذكرة المطلوبة: شريط تنبيه فوق بدل القفل.
    if (currentPending) {
        return (
            <div style={{ position: 'sticky', top: 0, zIndex: 60, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 14, background: 'linear-gradient(90deg, #4f46e5, #7c3aed)', color: '#fff', boxShadow: '0 8px 24px rgba(79,70,229,.25)' }}>
                <FaCommentDots size={18} style={{ flexShrink: 0 }} />
                <div style={{ fontSize: 13, fontWeight: 800, lineHeight: 1.6 }}>
                    {isAr
                        ? `مطلوب منك تعليق على التذكرة #${currentPending.ticketNumber} — باقي الصفحات مقفولة لحد ما تضيف تعليقك في خانة الرد بالأسفل.`
                        : `A comment is required on ticket #${currentPending.ticketNumber} — other pages stay locked until you add your comment below.`}
                </div>
                {pending.length > 1 && (
                    <span style={{ marginInlineStart: 'auto', padding: '3px 10px', borderRadius: 999, background: 'rgba(255,255,255,.2)', fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap' }}>
                        {isAr ? `${pending.length - 1} تذكرة أخرى` : `${pending.length - 1} more`}
                    </span>
                )}
            </div>
        );
    }

    if (!blocked) return null;

    return (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,.72)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ width: '100%', maxWidth: 520, background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 30px 80px rgba(0,0,0,.35)' }}>
                <div style={{ padding: '22px 22px 16px', background: 'linear-gradient(135deg, #312e81, #6d28d9)', color: '#fff', textAlign: 'center' }}>
                    <span style={{ width: 54, height: 54, borderRadius: 16, background: 'rgba(255,255,255,.15)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
                        <FaLock size={22} />
                    </span>
                    <div style={{ fontSize: 17, fontWeight: 900 }}>{isAr ? 'مطلوب منك إجراء أولاً' : 'Action required first'}</div>
                    <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85, lineHeight: 1.7 }}>
                        {isAr
                            ? 'برجاء التوجه للتذكرة وإضافة تعليق عليها. باقي النظام هيتفتح تلقائياً بعد التعليق.'
                            : 'Please open the ticket and add a comment. The rest of the system unlocks automatically after you comment.'}
                    </div>
                </div>
                <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: '50vh', overflowY: 'auto' }}>
                    {pending.map((ticket) => (
                        <button
                            key={ticket._id}
                            type="button"
                            onClick={() => goTo(ticket)}
                            style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '12px 14px', borderRadius: 14, border: '1.5px solid #e0e7ff', background: '#f5f7ff', cursor: 'pointer', textAlign: 'start' }}
                        >
                            <span style={{ width: 38, height: 38, borderRadius: 12, background: '#4f46e5', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <FaRobot size={16} />
                            </span>
                            <span style={{ minWidth: 0, flex: 1 }}>
                                <span style={{ display: 'block', fontSize: 12, fontWeight: 900, color: '#4f46e5' }}>{isAr ? 'تذكرة' : 'Ticket'} #{ticket.ticketNumber}</span>
                                <span dir="auto" style={{ display: 'block', fontSize: 13, fontWeight: 800, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{localizedText(ticket, 'subject', lang)}</span>
                            </span>
                            <span style={{ padding: '6px 12px', borderRadius: 999, background: '#4f46e5', color: '#fff', fontSize: 12, fontWeight: 900, whiteSpace: 'nowrap' }}>
                                {isAr ? 'اذهب وعلّق' : 'Go & comment'}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PendingActionGuard
